import React, { useState } from 'react';
import { projectsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Plus, X, Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Editor reutilizável de Laudos agendados (Fase 3 + complemento Fase 4).
 * Usado tanto em ProjectTimeline (etapa Projeto Creditado) quanto em
 * ArchivedProjects (linha expandida), permitindo marcar realizado / remover / adicionar.
 *
 * Props:
 *  - projectId
 *  - initialLaudos (default [])
 *  - readOnly (default false)
 *  - compact (default false) — sem o border-top espaçado
 */
const LaudosEditor = ({
  projectId,
  initialLaudos = [],
  readOnly = false,
  compact = false,
}) => {
  const [laudos, setLaudos] = useState(initialLaudos || []);
  const [form, setForm] = useState({ data: '', descricao: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!form.data || !form.descricao.trim()) {
      toast.error('Informe data e descrição do laudo');
      return;
    }
    setSaving(true);
    try {
      const res = await projectsAPI.addLaudo(projectId, {
        data: form.data,
        descricao: form.descricao.trim(),
      });
      setLaudos((prev) => [...prev, res.data].sort((a, b) =>
        (a.data || '').localeCompare(b.data || '')));
      setForm({ data: '', descricao: '' });
      toast.success('Laudo adicionado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao adicionar laudo');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRealizado = async (id) => {
    try {
      const res = await projectsAPI.toggleLaudoRealizado(projectId, id);
      setLaudos((prev) => prev.map((l) => (l.id === id ? res.data : l)));
      toast.success(res.data.realizado ? 'Laudo marcado como realizado' : 'Laudo desmarcado');
    } catch {
      toast.error('Erro ao alterar status do laudo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este laudo?')) return;
    try {
      await projectsAPI.deleteLaudo(projectId, id);
      setLaudos((prev) => prev.filter((l) => l.id !== id));
      toast.success('Laudo removido');
    } catch {
      toast.error('Erro ao remover laudo');
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sorted = [...laudos].sort((a, b) => (a.data || '').localeCompare(b.data || ''));

  return (
    <div className={cn('space-y-3', !compact && 'pt-3 border-t')} data-testid="laudos-editor">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">Datas para Laudos</Label>
        <span className="text-xs text-muted-foreground">
          {laudos.length} agendado(s)
          {laudos.length > 0 && ` · ${laudos.filter((l) => l.realizado).length} realizado(s)`}
        </span>
      </div>

      {sorted.length > 0 && (
        <div className="space-y-1.5">
          {sorted.map((l) => {
            const ld = l.data ? new Date(`${l.data}T00:00:00`) : null;
            const isPast = ld && ld < today;
            const isToday = ld && ld.toDateString() === today.toDateString();
            return (
              <div
                key={l.id}
                className={cn(
                  'flex items-start gap-2 p-2 rounded border text-sm',
                  l.realizado ? 'bg-emerald-500/5 border-emerald-500/20' :
                  isToday ? 'bg-amber-500/10 border-amber-500/30' :
                  isPast ? 'bg-red-500/5 border-red-500/30' :
                  'bg-blue-500/5 border-blue-500/20'
                )}
                data-testid={`laudo-item-${l.id}`}
              >
                <Checkbox
                  checked={!!l.realizado}
                  onCheckedChange={() => handleToggleRealizado(l.id)}
                  className="mt-0.5"
                  data-testid={`laudo-realizado-${l.id}`}
                />
                <CalendarIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('font-mono text-xs font-semibold', l.realizado && 'line-through text-muted-foreground')}>
                      {ld ? ld.toLocaleDateString('pt-BR') : l.data}
                    </span>
                    {isToday && !l.realizado && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white font-medium">HOJE</span>}
                    {isPast && !l.realizado && !isToday && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-medium">ATRASADO</span>}
                    {l.realizado && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-medium">REALIZADO</span>}
                  </div>
                  <p className={cn('break-words mt-0.5', l.realizado && 'line-through text-muted-foreground')}>
                    {l.descricao}
                  </p>
                  {l.criado_por_nome && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      agendado por {l.criado_por_nome}
                      {l.realizado && l.data_realizado && (
                        <> · realizado em {new Date(l.data_realizado).toLocaleString('pt-BR')}</>
                      )}
                    </p>
                  )}
                </div>
                {!readOnly && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 flex-shrink-0"
                    onClick={() => handleDelete(l.id)}
                    data-testid={`laudo-delete-${l.id}`}
                    title="Remover laudo"
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && (
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="date"
            value={form.data}
            onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))}
            className="sm:w-40"
            data-testid="laudo-data-input"
          />
          <Input
            placeholder="Descrição do laudo (ex: Laudo Técnico Inicial)"
            value={form.descricao}
            onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            className="flex-1"
            data-testid="laudo-descricao-input"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          />
          <Button onClick={handleAdd} disabled={saving} size="sm" data-testid="laudo-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Agendar
          </Button>
        </div>
      )}
    </div>
  );
};

export default LaudosEditor;
